/**
 * Behavioral Campaign Operating System
 * ---------------------------------------------------------------
 * Orchestrates multi-asset behavioral campaigns from the existing
 * intelligence layers:
 *   - asset_predictions       (forecasted virality / replay)
 *   - content_assets          (hook / format / archetype / escalation)
 *   - platform_signal_ingestion (live performance)
 *   - generation_biases       (active intelligence directives)
 *
 * Output: a `behavioral_campaigns` row + an ordered chain of
 * `campaign_asset_sequences` modeling a 5-stage narrative arc:
 *   Hook Ambiguity → Behavioral Reveal → Escalation Confrontation
 *   → POV Reframing → Replay Reinforcement
 *
 * Campaign performance is fed BACK into generation_biases so the
 * orchestrator and prediction engine evolve from real outcomes.
 */
import { supabase } from "@/integrations/supabase/client";

export const CAMPAIGN_CREATED_EVENT = "greyrock:campaign-created";
export const CAMPAIGN_UPDATED_EVENT = "greyrock:campaign-updated";

export type StrategicRole =
  | "hook_ambiguity"
  | "behavioral_reveal"
  | "escalation_confrontation"
  | "pov_reframing"
  | "replay_reinforcement";

export const ROLE_SEQUENCE: StrategicRole[] = [
  "hook_ambiguity",
  "behavioral_reveal",
  "escalation_confrontation",
  "pov_reframing",
  "replay_reinforcement",
];

export const ROLE_LABEL: Record<StrategicRole, string> = {
  hook_ambiguity: "Hook Ambiguity",
  behavioral_reveal: "Behavioral Reveal",
  escalation_confrontation: "Escalation Confrontation",
  pov_reframing: "POV Reframing",
  replay_reinforcement: "Replay Reinforcement",
};

export const ROLE_PACING: Record<StrategicRole, string> = {
  hook_ambiguity: "slow-burn",
  behavioral_reveal: "measured",
  escalation_confrontation: "rapid",
  pov_reframing: "reflective",
  replay_reinforcement: "looping",
};

export interface CampaignSequenceRow {
  asset_id: string;
  sequence_order: number;
  strategic_role: StrategicRole;
  escalation_position: string;
  pacing_type: string;
  projected_retention_impact: number;
  projected_replay_impact: number;
  is_reinforcement: boolean;
  is_escalation_pivot: boolean;
}

export interface CampaignProposal {
  campaign_name: string;
  objective: string;
  dominant_platform: string | null;
  dominant_archetype: string | null;
  dominant_escalation: string | null;
  target_behavior: string;
  projected_virality: number;
  projected_retention: number;
  audience_conditioning_score: number;
  fatigue_risk: number;
  rationale: string;
  sequences: CampaignSequenceRow[];
}

const clamp = (n: number, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, n));
const avg = (a: number[]) => (a.length ? a.reduce((s, n) => s + n, 0) / a.length : 0);

const ROLE_BEHAVIORAL_PROFILE: Record<
  StrategicRole,
  { archetypes: string[]; formats: string[]; escalations: string[]; intensity: string[] }
> = {
  hook_ambiguity: {
    archetypes: ["ambiguity", "withdrawal", "guilt"],
    formats: ["hook", "narration", "voiceover"],
    escalations: ["minimization", "deflection"],
    intensity: ["low", "medium"],
  },
  behavioral_reveal: {
    archetypes: ["deflection", "guilt", "blame_shifting"],
    formats: ["pov", "voiceover", "skit"],
    escalations: ["projection", "deflection"],
    intensity: ["medium"],
  },
  escalation_confrontation: {
    archetypes: ["rage", "blame_shifting", "intimidation"],
    formats: ["pov", "skit", "confrontation"],
    escalations: ["aggression", "escalation"],
    intensity: ["high"],
  },
  pov_reframing: {
    archetypes: ["healthy_boundary", "grey_rock"],
    formats: ["pov", "narration"],
    escalations: ["boundary", "containment"],
    intensity: ["medium"],
  },
  replay_reinforcement: {
    archetypes: ["healthy_boundary", "grey_rock", "withdrawal"],
    formats: ["narration", "voiceover", "loop"],
    escalations: ["containment", "minimization"],
    intensity: ["low", "medium"],
  },
};

interface AssetCandidate {
  id: string;
  archetype: string | null;
  format: string | null;
  escalation_path: string | null;
  behavioral_intensity: string | null;
  platform: string | null;
  hook: string | null;
  scenario_title: string | null;
  prediction?: { virality: number; replay: number; completion: number; save: number; confidence: number };
}

const scoreFit = (a: AssetCandidate, role: StrategicRole) => {
  const p = ROLE_BEHAVIORAL_PROFILE[role];
  let s = 0;
  if (a.archetype && p.archetypes.includes(a.archetype)) s += 40;
  if (a.format && p.formats.some((f) => a.format!.toLowerCase().includes(f))) s += 25;
  if (a.escalation_path && p.escalations.some((e) => a.escalation_path!.toLowerCase().includes(e))) s += 20;
  if (a.behavioral_intensity && p.intensity.includes(a.behavioral_intensity)) s += 10;
  if (a.prediction) s += Math.min(25, a.prediction.virality / 4);
  return s;
};

/** Build (without persisting) the next campaign proposal. */
export async function proposeCampaign(opts: {
  platformBias?: string | null;
  objective?: string;
} = {}): Promise<CampaignProposal> {
  const [{ data: assetsRaw }, { data: predsRaw }, { data: teleRaw }] = await Promise.all([
    (supabase as any)
      .from("content_assets")
      .select("id, archetype, format, escalation_path, behavioral_intensity, platform, hook, scenario_title, created_at")
      .order("created_at", { ascending: false })
      .limit(300),
    (supabase as any)
      .from("asset_predictions")
      .select("asset_id, predicted_virality_score, predicted_replay_rate, predicted_completion_rate, predicted_save_rate, confidence")
      .order("created_at", { ascending: false })
      .limit(800),
    (supabase as any)
      .from("platform_signal_ingestion")
      .select("platform, archetype, virality_score, replay_rate")
      .order("collected_at", { ascending: false })
      .limit(200),
  ]);

  const assets = (assetsRaw ?? []) as any[];
  const preds = (predsRaw ?? []) as any[];
  const tele = (teleRaw ?? []) as any[];

  const predMap = new Map<string, AssetCandidate["prediction"]>();
  preds.forEach((p) => {
    if (predMap.has(p.asset_id)) return;
    predMap.set(p.asset_id, {
      virality: Number(p.predicted_virality_score) || 0,
      replay: Number(p.predicted_replay_rate) || 0,
      completion: Number(p.predicted_completion_rate) || 0,
      save: Number(p.predicted_save_rate) || 0,
      confidence: Number(p.confidence) || 0,
    });
  });

  const candidates: AssetCandidate[] = assets.map((a) => ({ ...a, prediction: predMap.get(a.id) }));

  // ── Determine dominant platform / archetype from telemetry + predictions ──
  const platformVir: Record<string, number[]> = {};
  tele.forEach((t) => {
    if (!t.platform) return;
    (platformVir[t.platform] ??= []).push(Number(t.virality_score) || 0);
  });
  let dominantPlatform: string | null = opts.platformBias ?? null;
  if (!dominantPlatform) {
    let best = -1;
    Object.entries(platformVir).forEach(([p, vs]) => {
      const v = avg(vs);
      if (v > best) { best = v; dominantPlatform = p; }
    });
  }

  const archCounts: Record<string, number> = {};
  candidates.forEach((c) => {
    if (!c.archetype) return;
    archCounts[c.archetype] = (archCounts[c.archetype] ?? 0) + (c.prediction?.virality ?? 30);
  });
  const dominantArchetype = Object.entries(archCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  // ── For each strategic role, pick best-fit asset (no duplicates) ──
  const used = new Set<string>();
  const sequences: CampaignSequenceRow[] = [];

  ROLE_SEQUENCE.forEach((role, idx) => {
    const ranked = candidates
      .filter((c) => !used.has(c.id))
      .filter((c) => !dominantPlatform || !c.platform || c.platform === dominantPlatform)
      .map((c) => ({ c, score: scoreFit(c, role) }))
      .sort((a, b) => b.score - a.score);
    const pick = ranked[0]?.c ?? candidates.find((c) => !used.has(c.id));
    if (!pick) return;
    used.add(pick.id);
    const repBase = pick.prediction?.replay ?? 45;
    const retBase = pick.prediction?.completion ?? 50;
    sequences.push({
      asset_id: pick.id,
      sequence_order: idx + 1,
      strategic_role: role,
      escalation_position: ["intro", "build", "peak", "release", "loop"][idx],
      pacing_type: ROLE_PACING[role],
      projected_retention_impact: Math.round(clamp(retBase * (idx === 2 ? 1.15 : idx === 4 ? 1.25 : 1.0))),
      projected_replay_impact: Math.round(clamp(repBase * (role === "replay_reinforcement" ? 1.35 : 1.0))),
      is_reinforcement: role === "replay_reinforcement",
      is_escalation_pivot: role === "escalation_confrontation",
    });
  });

  const projectedVirality = Math.round(clamp(avg(sequences.map((s) => s.projected_replay_impact)) * 0.55 + 25));
  const projectedRetention = Math.round(clamp(avg(sequences.map((s) => s.projected_retention_impact)) * 0.85));
  // Conditioning rises with role coverage; fatigue rises if too many high-intensity assets
  const conditioning = Math.round(clamp(sequences.length * 18 + (sequences.find((s) => s.is_reinforcement) ? 12 : 0)));
  const highIntensity = sequences.filter((s) => s.is_escalation_pivot || s.pacing_type === "rapid").length;
  const fatigue = Math.round(clamp(highIntensity * 22 - (sequences.find((s) => s.is_reinforcement) ? 15 : 0), 0, 95));

  const dominantEscalation = sequences[2]?.escalation_position ?? "peak";
  const cycleStamp = new Date().toISOString().slice(5, 16).replace("T", " ");
  const campaign_name = `Campaign · ${(dominantArchetype ?? "Pattern").toUpperCase()} × ${(dominantPlatform ?? "MULTI").toUpperCase()} · ${cycleStamp}`;
  const objective = opts.objective ?? `Condition audience around ${dominantArchetype ?? "behavioral"} pattern via 5-stage narrative arc`;
  const rationale = `Sequenced ${sequences.length} assets — pivot at stage 3 (${ROLE_LABEL.escalation_confrontation}), reinforcement at stage 5. Fatigue risk ${fatigue}%, conditioning ${conditioning}%.`;

  return {
    campaign_name,
    objective,
    dominant_platform: dominantPlatform,
    dominant_archetype: dominantArchetype,
    dominant_escalation: dominantEscalation,
    target_behavior: "replay + save + narrative continuation",
    projected_virality: projectedVirality,
    projected_retention: projectedRetention,
    audience_conditioning_score: conditioning,
    fatigue_risk: fatigue,
    rationale,
    sequences,
  };
}

/** Persist a campaign + feed accepted directives back into generation_biases. */
export async function commitCampaign(p: CampaignProposal): Promise<{ campaignId: string }> {
  const { data: campRow, error } = await (supabase as any)
    .from("behavioral_campaigns")
    .insert({
      campaign_name: p.campaign_name,
      objective: p.objective,
      dominant_platform: p.dominant_platform,
      dominant_archetype: p.dominant_archetype,
      dominant_escalation: p.dominant_escalation,
      target_behavior: p.target_behavior,
      projected_virality: p.projected_virality,
      projected_retention: p.projected_retention,
      audience_conditioning_score: p.audience_conditioning_score,
      fatigue_risk: p.fatigue_risk,
      rationale: p.rationale,
      status: "active",
      metadata: { source: "campaign-engine" },
    })
    .select("id")
    .single();
  if (error) throw error;
  const campaignId = campRow.id as string;

  if (p.sequences.length > 0) {
    const rows = p.sequences.map((s) => ({ ...s, campaign_id: campaignId }));
    await (supabase as any).from("campaign_asset_sequences").insert(rows);
  }

  // Feed back into generation_biases — amplify dominant archetype/platform
  const biasInserts: any[] = [];
  if (p.dominant_archetype) {
    biasInserts.push({
      dimension: "archetype",
      key: p.dominant_archetype,
      bias_direction: "increase",
      weight: 0.8,
      confidence: 70,
      evidence_score: p.projected_virality,
      impact_score: p.projected_retention,
      rationale: `Campaign "${p.campaign_name}" amplifies ${p.dominant_archetype}.`,
      active: true,
    });
  }
  // Reinforce winning role-format
  const reinf = p.sequences.find((s) => s.is_reinforcement);
  if (reinf) {
    biasInserts.push({
      dimension: "format",
      key: ROLE_PACING.replay_reinforcement,
      bias_direction: "increase",
      weight: 0.6,
      confidence: 65,
      evidence_score: reinf.projected_replay_impact,
      impact_score: reinf.projected_replay_impact,
      rationale: `Replay reinforcement asset projected ${reinf.projected_replay_impact}% replay lift.`,
      active: true,
    });
  }
  if (biasInserts.length > 0) {
    await (supabase as any).from("generation_biases").insert(biasInserts);
  }

  window.dispatchEvent(new CustomEvent(CAMPAIGN_CREATED_EVENT, { detail: { campaignId } }));
  return { campaignId };
}

export async function fetchCampaigns() {
  const { data } = await (supabase as any)
    .from("behavioral_campaigns")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(20);
  return (data ?? []) as any[];
}

export async function fetchCampaignSequences(campaignId: string) {
  const { data } = await (supabase as any)
    .from("campaign_asset_sequences")
    .select("*")
    .eq("campaign_id", campaignId)
    .order("sequence_order", { ascending: true });
  return (data ?? []) as any[];
}

export async function attachAssetToCampaign(opts: {
  campaignId: string;
  assetId: string;
  role: StrategicRole;
  isReinforcement?: boolean;
  isPivot?: boolean;
}) {
  const seqs = await fetchCampaignSequences(opts.campaignId);
  const nextOrder = (seqs[seqs.length - 1]?.sequence_order ?? 0) + 1;
  await (supabase as any).from("campaign_asset_sequences").insert({
    campaign_id: opts.campaignId,
    asset_id: opts.assetId,
    sequence_order: nextOrder,
    strategic_role: opts.role,
    pacing_type: ROLE_PACING[opts.role],
    escalation_position: ["intro", "build", "peak", "release", "loop"][Math.min(4, nextOrder - 1)],
    is_reinforcement: !!opts.isReinforcement,
    is_escalation_pivot: !!opts.isPivot,
    projected_retention_impact: 55,
    projected_replay_impact: 55,
  });
  window.dispatchEvent(new CustomEvent(CAMPAIGN_UPDATED_EVENT, { detail: { campaignId: opts.campaignId } }));
}

export async function reorderSequence(seqId: string, newOrder: number) {
  await (supabase as any).from("campaign_asset_sequences").update({ sequence_order: newOrder }).eq("id", seqId);
  window.dispatchEvent(new CustomEvent(CAMPAIGN_UPDATED_EVENT));
}

export async function setSequenceFlags(seqId: string, flags: { is_reinforcement?: boolean; is_escalation_pivot?: boolean }) {
  await (supabase as any).from("campaign_asset_sequences").update(flags).eq("id", seqId);
  window.dispatchEvent(new CustomEvent(CAMPAIGN_UPDATED_EVENT));
}
